//* Hooks Imports
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

//* Libraries Imports 
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router-dom";

//* Imports Components
import { Form, FormControl as Input, InputGroup } from "react-bootstrap";
import PasswordInput from "../../../component/base/PasswordInput";
import DefaultButton from "../../../component/base/DefaultButton";

//* Imports Schemas
import { loginSchema, type LoginFormData } from "../../../schemas/authSchemas";

//* Imports Services & Utils
import { AuthAPI } from "../../../services/apis/AuthAPI";

//* Import Redux actions separately to ensure proper initialization
import { setTokenAndMe } from "../../../services/stores/auth/actions";

export function LoginForm() {
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [loginSuccess, setLoginSuccess] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      cpf: "",
      password: "",
    },
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { logged } = useSelector((store: any) => store.auth);

  useEffect(() => {
    if (loginSuccess && logged) {
      navigate("/");
    }
  }, [loginSuccess, logged, navigate]);

  const cpfValue = watch("cpf");

  // Format CPF input
  const handleCpfChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value.replace(/\D/g, "");
    
    let formattedValue = rawValue;
    if (rawValue.length <= 11) {
      formattedValue = rawValue
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    }

    setValue("cpf", formattedValue, { shouldValidate: true });
  };
    const onSubmit = async (data: LoginFormData) => {
    setLoading(true);
    setErrorMessage("");

    try {
      const response = await AuthAPI.authorize(data.cpf, data.password);

      if (response.result && response.data?.user) {
        const { user } = response.data;
        const { access } = response.data;
        
        if (!user || !access?.token) {
          setErrorMessage("Dados de usuário incompletos. Tente novamente.");
          return;
        }      
        dispatch(
          setTokenAndMe({
            me: user,
            token: access.token,
          })
        );

        setLoginSuccess(true);
      } else {
        setErrorMessage(response.message || "Erro ao fazer login. Verifique suas credenciais.");
      }
    } catch (error) {
      setErrorMessage("Erro inesperado ao fazer login");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  return (
    <form
      className="form-horizontal mt-4"
      onSubmit={handleSubmit(onSubmit)}
    >     
    
     <InputGroup className="mb-3">
        <div className="flex fdcolumn flex1 w-100">
          <Form.Label>
            CPF
          </Form.Label>          
          <Input
            type="text"
            className="form-control fs-6"
            placeholder="000.000.000-00"
            {...register("cpf")}
            onChange={handleCpfChange}
            value={cpfValue}
            autoComplete="username"
            maxLength={14}
          />
          {errors.cpf && (
            <span className="text-danger small mt-1">
              {errors.cpf.message}
            </span>
          )}
        </div>
      </InputGroup>

      <InputGroup className="mb-3">
        <div className="flex fdcolumn flex1 w-100">          
        <PasswordInput
            label="Senha"
            placeholder="Digite sua senha"
            className="flex1"
            {...register("password")}
          />
          {errors.password && (
            <span className="text-danger small mt-1">
              {errors.password.message}
            </span>
          )}
        </div>
      </InputGroup>

      {/* Esqueci minha senha estilizado */}
      <div className="mt-1 mb-3 text-start">
        <Link
          to="/forgot-password"
          className="small fw-bold text-primary text-decoration-none"
        >
          Esqueci minha senha
        </Link>
      </div>

      <div className="form-group row mt-4 mb-2">
        <div className="col-sm-12">
          {errorMessage && (
            <div 
              className="alert alert-danger py-2 text-center" 
              role="alert"
            >
              <small>{errorMessage}</small>
            </div>
          )}
        </div>
        <div className="col-sm-12 text-end">
          <DefaultButton
            type="submit"
            bg="primary"
            width="100%"
            height="3rem"
            className="mt-2"
            text="Entrar"
            loadingtext="Entrando..."
            loading={loading}
          />
        </div>
      </div>
    </form>
  );
};
